import { useState } from 'react';
import { api, unwrap, type Schemas } from '../../api/client';
import { useApiQuery, useInvalidate } from '../../api/hooks';
import { canAdmin, useTenant } from '../../auth/session';
import { errorText, Icon, Modal, useToast } from '../../ui/kit';
import { fmtDate } from '../../lib/format';
import { copyText } from './data';
import { APP_NAME } from '../../lib/brand';

type CompanyPractice = Schemas['CompanyPractice'];
type PendingInvitation = Schemas['AccountantInvitation'];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The practice that keeps this company's books (ADR 0043), for Team and the dashboard. */
export function useCompanyPractice() {
  // An employee is refused it (ADR 0023), so it is not asked for.
  const tenant = useTenant();
  return useApiQuery(['company-practice'], async () => ((await unwrap(api.GET('/v1/tenant/practice'))).data ?? []) as CompanyPractice[],
    { staleTime: 60_000, enabled: tenant.role !== 'employee' });
}

/**
 * Settings → Team: the accountant. The practice shows as "Fynbos Accounting (practice)" with the
 * role Accountant and Remove access; without one, an owner or admin invites their accountant
 * (PRACTICE-DESIGN.md 3A and 7).
 */
export function AccountantPanel() {
  const tenant = useTenant();
  const admin = canAdmin(tenant.role);
  const link = useCompanyPractice();
  const invalidate = useInvalidate();
  const toast = useToast();
  const [inviting, setInviting] = useState(false);
  const [removing, setRemoving] = useState<CompanyPractice | null>(null);
  const [busy, setBusy] = useState(false);
  const current = (link.data ?? [])[0] ?? null;
  const pending = useApiQuery(['accountant-invitation'], async () => ((await unwrap(api.GET('/v1/tenant/accountant-invitation'))).data ?? null) as PendingInvitation | null,
    { staleTime: 60_000, enabled: admin && !current && !link.isLoading });
  const invitation = pending.data && !pending.data.expired ? pending.data : null;

  const withdraw = async (p: PendingInvitation) => {
    setBusy(true);
    try {
      await unwrap(api.POST('/v1/tenant/accountant-invitation/{id}/revoke', { params: { path: { id: p.id as string } } }));
      await invalidate('accountant-invitation');
      toast('Invitation withdrawn');
    } catch (e) {
      toast(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p: CompanyPractice) => {
    setBusy(true);
    try {
      await unwrap(api.POST('/v1/tenant/practice/{linkId}/remove', { params: { path: { linkId: p.id as string } } }));
      await invalidate('company-practice');
      toast(p.state === 'requested' ? 'Request withdrawn' : `${p.practice_name} no longer has access`);
      setRemoving(null);
    } catch (e) {
      toast(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="card-head" style={{ paddingBottom: 12 }}>
        <h2>Your accountant</h2>
        {!current && !invitation && admin && <button className="btn small" style={{ marginLeft: 'auto' }} onClick={() => setInviting(true)}><Icon name="plus" size={15} />Invite your accountant</button>}
      </div>
      {link.isLoading ? null : !current && invitation ? (
        <div className="table-wrap">
          <table className="team-table">
            <tbody>
              <tr>
                <td><b>{invitation.email}</b><div className="secondary">Invited {fmtDate((invitation.created_at ?? '').slice(0, 10))} · waiting for them to accept</div></td>
                <td><span className="pill status pending">Invited</span></td>
                <td className="role-desc hide-sm">They get the Accountant role when they accept. If they have no practice yet, they set one up first.</td>
                <td className="r"><button className="btn small ghost" disabled={busy} onClick={() => void withdraw(invitation)}>Withdraw</button><button className="btn small ghost" disabled={busy} onClick={() => setInviting(true)}>Send again</button></td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : !current ? (
        <p className="muted team-foot">{admin ? `Invite your accountant's practice to keep your books in ${APP_NAME}. You can remove their access at any time.` : 'No accountant has access. An owner or admin can invite one.'}</p>
      ) : (
        <div className="table-wrap">
          <table className="team-table">
            <tbody>
              <tr>
                <td><b>{current.practice_name} (practice)</b>
                  <div className="secondary">
                    {current.state === 'requested' ? `Asked ${current.requested_at ? fmtDate(current.requested_at.slice(0, 10)) : ''} · waiting for them to accept`
                      : current.state === 'practice_managed' ? 'Set up this company for you'
                      : current.client_since ? `Since ${fmtDate(current.client_since)}` : 'Keeps your books'}
                  </div>
                </td>
                <td>{current.state === 'requested' ? <span className="pill status pending">Invited</span> : 'Accountant'}</td>
                <td className="role-desc hide-sm">Everything in the books, questions and documents. Never your plan, owners or team.</td>
                <td className="r">{admin && !current.via_practice && current.state !== 'practice_managed' && (
                  <button className="btn small ghost" onClick={() => setRemoving(current)}>{current.state === 'requested' ? 'Withdraw' : 'Remove access'}</button>
                )}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      {inviting && <InviteAccountantModal onClose={() => setInviting(false)} />}
      {removing && (
        <Modal title={removing.state === 'requested' ? 'Withdraw the request?' : `Remove ${removing.practice_name as string}?`} onClose={() => setRemoving(null)} onSubmit={() => void remove(removing)}
          footer={<><button type="button" className="btn" onClick={() => setRemoving(null)}>Cancel</button><button type="submit" className="btn primary danger" disabled={busy}>{removing.state === 'requested' ? 'Withdraw' : 'Remove access'}</button></>}>
          <p style={{ marginTop: 0 }}>{removing.state === 'requested'
            ? `${removing.practice_name} will not be able to accept it.`
            : `${removing.practice_name} can no longer open your books, straight away. Everything they did stays in your books, and you can invite them again later.`}</p>
        </Modal>
      )}
    </div>
  );
}

function InviteAccountantModal({ onClose }: { onClose: () => void }) {
  const invalidate = useInvalidate();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState<{ email: string; url: string; emailStatus: string | undefined } | null>(null);

  const submit = async () => {
    setError('');
    if (!EMAIL_RE.test(email.trim())) { setError('Enter your accountant’s email address.'); return; }
    setBusy(true);
    try {
      const res = await unwrap(api.POST('/v1/tenant/accountant-invitation', { body: { email: email.trim() } }));
      await invalidate('accountant-invitation');
      // Always offer the link, so the invitation can be shared by hand while email is not set up.
      setIssued({ email: email.trim(), url: res.data?.accept_url ?? '', emailStatus: res.data?.email_status });
      setBusy(false);
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
    }
  };

  if (issued) {
    const why = issued.emailStatus === 'sent' ? `We emailed ${issued.email}. You can also send them this link yourself.`
      : issued.emailStatus === 'failed' ? 'The mail service could not send the email.'
      : `Email is not set up for ${APP_NAME} yet, so no email went out.`;
    return (
      <Modal title="Share the invite link" onClose={onClose} onSubmit={onClose}
        footer={<><span className="spacer" /><button type="button" className="btn" onClick={async () => { if (await copyText(issued.url)) toast('Invite link copied'); else toast('Select the link and copy it'); }}><Icon name="doc" size={15} />Copy link</button><button type="submit" className="btn primary">Done</button></>}>
        <p className="dialog-copy">{why} The link works only for <b>{issued.email}</b>, for 14 days. Sending a new invitation replaces it, and the link cannot be shown again later.</p>
        <div className="field"><label htmlFor="acc-link">Invite link</label><input className="input" id="acc-link" readOnly value={issued.url} onFocus={(e) => e.currentTarget.select()} /></div>
      </Modal>
    );
  }
  return (
    <Modal title="Invite your accountant" onClose={onClose} onSubmit={() => void submit()}
      footer={<><button type="button" className="btn" onClick={onClose}>Cancel</button><button type="submit" className="btn primary" disabled={busy}>Send invitation</button></>}>
      <p className="muted" style={{ marginTop: 0 }}>We email them a link. When they accept, their practice gets the Accountant role: everything in the books, questions and documents. They cannot change your plan, your owners or your team, and you can remove them at any time.</p>
      <div className="field"><label htmlFor="acc-email">Your accountant's email</label><input id="acc-email" className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={320} autoFocus /><span className="hint">If they have no practice on {APP_NAME} yet, they set one up when they accept</span></div>
      {error && <p className="error" role="alert" style={{ color: 'var(--critical-text)', fontSize: 13 }}>{error}</p>}
    </Modal>
  );
}
